import { useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ArrowLeftRight, CalendarClock, Check, Clock, HandHeart, ScanLine, Shirt, Users, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, fmtDate, fileUrl } from "@/lib/api";
import { PageTitle, useApi, Skeleton, Stagger, Item, badge, inputCls } from "@/components/portal/ui";
import MinistryLeaderboard from "@/pages/portal/Leaderboard";
import MyAwards from "@/pages/portal/Piagam";

function DressCodeModal({ m, onClose }) {
  return createPortal(
    <div className="fixed inset-0 z-[80] grid place-items-center bg-slate-900/60 backdrop-blur-sm p-4" data-testid="dresscode-modal" onClick={onClose}>
      <div className="w-full max-w-sm bg-white rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="bg-slate-900 text-white p-5 flex items-start justify-between">
          <div><p className="text-[11px] font-black uppercase tracking-widest text-amber-300">Dress Code</p><p className="font-extrabold mt-0.5">{m.ministry_name}</p></div>
          <button onClick={onClose} data-testid="dresscode-close" className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5">
          {m.dress_code ? <p className="text-sm text-slate-700 whitespace-pre-line leading-relaxed">{m.dress_code}</p>
            : <p className="text-sm text-slate-400">Admin belum mengatur dress code untuk pelayanan ini.</p>}
          {(m.dress_images || []).length > 0 && (
            <div className="mt-4 grid grid-cols-2 gap-3">
              {m.dress_images.map((img, i) => <img key={i} src={fileUrl(img)} alt="referensi busana" className="w-full rounded-xl border border-slate-100 object-cover" data-testid={`dresscode-image-${i}`} />)}
            </div>
          )}
        </div>
      </div>
    </div>, document.body);
}

const STATUS = { assigned: ["warn", "Perlu konfirmasi"], confirmed: ["success", "Siap melayani"], declined: ["danger", "Berhalangan"] };

function ScheduleCard({ s, colleagues, onAction }) {
  const [swap, setSwap] = useState(false);
  const [to, setTo] = useState("");
  const [tone, label] = STATUS[s.status] || ["info", s.status];
  const peers = colleagues.filter((c) => c.ministry_id === s.ministry_id);
  return (
    <div className="bg-white rounded-3xl p-5 border border-slate-100 shadow-sm" data-testid={`ministry-schedule-${s.id}`}>
      <div className="flex items-center justify-between"><span className={`text-[11px] font-bold rounded-full px-3 py-1 ${badge[tone]}`}>{label}</span><span className="text-xs text-slate-500">{s.role}</span></div>
      <p className="mt-3 font-extrabold text-slate-900">{s.service}</p>
      <p className="text-xs text-slate-500 flex items-center gap-1 mt-1"><Clock className="w-3.5 h-3.5" />{fmtDate(s.date, { weekday: "long", day: "numeric", month: "short" })} · Call time {s.call_time}</p>
      {s.swapped_from && <p className="text-[11px] text-indigo-600 font-semibold mt-1">Hasil tukar jadwal</p>}
      <div className="mt-4 flex gap-2">
        {s.status !== "confirmed" && <button onClick={() => onAction("confirm", s, { status: "confirmed" })} data-testid={`ministry-confirm-${s.id}`} className="flex-1 inline-flex items-center justify-center gap-1.5 bg-emerald-500 text-white rounded-xl py-2.5 text-sm font-bold active:scale-95 transition"><Check className="w-4 h-4" />Hadir</button>}
        {s.status !== "declined" && <button onClick={() => onAction("confirm", s, { status: "declined" })} data-testid={`ministry-decline-${s.id}`} className="inline-flex items-center justify-center bg-red-50 text-red-600 rounded-xl px-3 active:scale-95 transition"><X className="w-4 h-4" /></button>}
        <button onClick={() => setSwap(!swap)} data-testid={`ministry-swap-toggle-${s.id}`} className="inline-flex items-center justify-center gap-1.5 border border-slate-200 rounded-xl px-3 text-sm font-bold text-slate-700 active:scale-95 transition"><ArrowLeftRight className="w-4 h-4" />Tukar</button>
      </div>
      {swap && (
        <div className="mt-3 flex gap-2">
          <select className={`${inputCls} !py-2.5 text-sm`} value={to} onChange={(e) => setTo(e.target.value)} data-testid={`ministry-swap-select-${s.id}`}>
            <option value="">Pilih rekan pelayanan…</option>{peers.map((c) => <option key={c.user_id} value={c.user_id}>{c.name} · {c.role}</option>)}
          </select>
          <button disabled={!to} onClick={() => onAction("swap", s, { to_user_id: to }).then(() => setSwap(false))} data-testid={`ministry-swap-send-${s.id}`} className="shrink-0 bg-blue-600 text-white rounded-xl px-4 text-sm font-bold disabled:opacity-50">Kirim</button>
        </div>
      )}
    </div>
  );
}

export default function MyMinistry() {
  const { data, reload } = useApi("/portal/ministry");
  const { data: cat } = useApi("/portal/ministry/categories");
  const { data: usher } = useApi("/portal/usher/assignment");
  const [dress, setDress] = useState(null);

  const onAction = async (kind, s, body) => {
    try {
      await api.post(`/portal/ministry/schedules/${s.id}/${kind}`, body);
      toast.success(kind === "swap" ? "Permintaan tukar terkirim" : "Konfirmasi tersimpan");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const respond = async (sw, accept) => {
    try {
      await api.post(`/portal/ministry/swaps/${sw.id}/respond`, { accept });
      toast.success(accept ? "Jadwal otomatis dipindahkan ke Anda" : "Permintaan ditolak");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div data-testid="portal-ministry">
      <PageTitle eyebrow="Serving" title="My Ministry" />
      <div className="px-5 mt-2">
        <Link to="/portal/serve-up" data-testid="ministry-serve-up-link" className="w-full inline-flex items-center justify-center gap-2 bg-slate-900 text-white rounded-2xl py-3 text-sm font-bold active:scale-95 transition"><HandHeart className="w-4 h-4 text-amber-300" />Serve-Up · Daftar Pelayanan</Link>
      </div>
      {usher?.is_usher ? (
        <div className="px-5 mt-3">
          <Link to="/portal/usher-scan" data-testid="ministry-usher-link" className="block rounded-3xl bg-blue-600 text-white p-5 active:scale-[0.99] transition">
            <ScanLine className="w-6 h-6 text-blue-100" />
            <p className="mt-3 font-extrabold">Scanner Usher</p>
            <p className="text-xs text-white/70 mt-0.5">Anda bertugas{usher.role ? ` sebagai ${usher.role}` : ""}. Pindai QR jemaat & tiket event di sini.</p>
          </Link>
        </div>
      ) : usher?.reason ? (
        <div className="px-5 mt-3">
          <div className="rounded-3xl bg-white border border-slate-200 p-5" data-testid="ministry-usher-locked">
            <ScanLine className="w-6 h-6 text-slate-300" />
            <p className="mt-3 font-extrabold text-slate-900">Scanner Usher Terkunci</p>
            <p className="text-xs text-slate-500 mt-0.5">{usher.reason}</p>
          </div>
        </div>
      ) : null}
      {cat?.applications?.filter((a) => a.status === "pending").map((a) => (
        <div key={a.id} className="px-5 mt-3" data-testid={`ministry-pending-${a.id}`}>
          <div className="rounded-2xl bg-amber-50 border border-amber-100 px-4 py-3 text-sm text-amber-800 font-semibold">Menunggu persetujuan: {a.ministry_name}{a.role ? ` · ${a.role}` : ""}</div>
        </div>
      ))}
      <div className="px-5 mt-4">
        {!data ? <Skeleton className="h-64" /> : (
          <Stagger className="space-y-4">
            {data.ministries.length === 0 && <Item className="bg-white rounded-3xl p-6 border border-slate-100 text-sm text-slate-500" data-testid="ministry-empty">Anda belum terdaftar di divisi pelayanan. Daftar lewat menu <b>Serve-Up</b> di atas.</Item>}
            {data.ministries.length > 0 && (
              <Item className="grid grid-cols-2 gap-3">
                {data.ministries.map((m) => (
                  <div key={m.id} className="rounded-3xl p-4 bg-slate-900 text-white flex flex-col" data-testid={`ministry-${m.ministry_id}`}>
                    <Users className="w-4 h-4 text-blue-300" />
                    <p className="mt-3 font-bold">{m.ministry_name}</p>
                    <p className="text-xs text-white/60">{m.scheduled_role || m.role}</p>
                    {m.scheduled ? (
                      <>
                        <span className="mt-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-300 bg-emerald-500/15 rounded-full px-2 py-0.5 self-start" data-testid={`ministry-scheduled-${m.ministry_id}`}>
                          <CalendarClock className="w-3 h-3" />Terjadwal {fmtDate(m.scheduled_date, { weekday: "short", day: "numeric", month: "short" })}
                        </span>
                        {m.scheduled_service && <span className="mt-1.5 inline-flex items-center gap-1 text-[10px] font-bold text-blue-200 bg-blue-500/15 rounded-full px-2 py-0.5 self-start" data-testid={`ministry-service-${m.ministry_id}`}><Clock className="w-3 h-3" />{m.scheduled_service}</span>}
                        <button onClick={() => setDress(m)} data-testid={`ministry-dresscode-${m.ministry_id}`} className="mt-3 inline-flex items-center gap-1.5 text-[11px] font-bold bg-white/15 hover:bg-white/25 rounded-full px-3 py-1.5 self-start active:scale-95 transition"><Shirt className="w-3.5 h-3.5 text-amber-300" />Dress Code</button>
                      </>
                    ) : (
                      <span className="mt-auto pt-3 text-[11px] text-white/40" data-testid={`ministry-unscheduled-${m.ministry_id}`}>Belum ada jadwal pelayanan</span>
                    )}
                  </div>
                ))}
                <div className="rounded-3xl p-4 bg-blue-600 text-white col-span-2 flex items-center justify-between" data-testid="ministry-attendance"><span className="text-sm font-semibold">Kehadiran pelayanan</span><span className="text-2xl font-black">{data.attendance.served}/{data.attendance.total}</span></div>
              </Item>
            )}
            {data.swaps_in.map((sw) => (
              <Item key={sw.id} className="rounded-3xl p-5 bg-amber-100 text-amber-700" data-testid={`swap-in-${sw.id}`}>
                <p className="font-bold">{sw.from_name} minta tukar jadwal</p>
                <p className="text-sm mt-1">{sw.service} · {fmtDate(sw.date, { day: "numeric", month: "short" })} · {sw.role} · call {sw.call_time}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => respond(sw, true)} data-testid={`swap-accept-${sw.id}`} className="flex-1 bg-emerald-500 text-white rounded-xl py-2.5 text-sm font-bold">Terima</button>
                  <button onClick={() => respond(sw, false)} data-testid={`swap-reject-${sw.id}`} className="flex-1 bg-white text-slate-700 rounded-xl py-2.5 text-sm font-bold">Tolak</button>
                </div>
              </Item>
            ))}
            <Item><MyAwards /></Item>
            <Item><MinistryLeaderboard /></Item>
            {data.upcoming.length > 0 && <Item><p className="px-1 text-sm font-bold text-slate-900">Jadwal Pelayanan</p></Item>}            {data.upcoming.map((s) => <Item key={s.id}><ScheduleCard s={s} colleagues={data.colleagues} onAction={onAction} /></Item>)}
            {data.swaps_out.length > 0 && <Item><p className="px-1 text-sm font-bold text-slate-900">Permintaan Tukar Saya</p></Item>}
            {data.swaps_out.map((sw) => <Item key={sw.id} className="bg-white rounded-2xl p-4 border border-slate-100 text-sm flex justify-between"><span>{sw.service} → {sw.to_name}</span><span className="font-bold text-slate-500">{sw.status}</span></Item>)}
          </Stagger>
        )}
      </div>
      {dress && <DressCodeModal m={dress} onClose={() => setDress(null)} />}
    </div>
  );
}
